import { create } from "zustand";
import { persist, createJSONStorage } from 'zustand/middleware'

const initialState = {
    items: [],
    selectedItems: [], // IDs of selected items
    shipping: null,
    PaymentMethod: null, // { Paqueteria, Servicio, Dias, ... }
    address: null,
    totals: {
        percentIVA: 0,
        subtotal: 0,
        shipping: 0,
        total: 0,
        itemCount: 0,
        discount: 0,
        balanceApplied: 0, // Saldo
        balanceAvailable: 0, // Saldo
    },
    cupon: {
        codigo: null,
        cuponData: null,
        descuentoAplicable: 0,
        aplicado: false
    },
    reglasAutomaticas: [], // Array de reglas aplicables, cada una con regla, descuentoAplicable, productosAplicables, idsMasters
    checkoutData: null, // To replace localStorage "ValoresParaCompra"
}

export const useOrderStore = create(persist((set, get) => ({
    orderState: initialState,
    setOrder: (order) => set((state) => ({ orderState: order })),
    setOrderState: (partialState) => set((state) => {
        const newOrderState = {
            ...state.orderState,
            ...partialState,
        };

        // Actualiza primero el estado
        const result = { orderState: newOrderState };

        // Y dentro del mismo tick recalcula_totales
        // usando el nuevo estado
        queueMicrotask(() => {
            get().calculateTotals();
        });

        return result;
    }),
    setTotalState: (partialState) => set((state) => {
        const order = { orderState: { ...state.orderState, totals: { ...state.orderState.totals, ...partialState } } }


        queueMicrotask(() => {
            get().calculateTotals();
        });
        return order;
    }),
    clearOrder: () => set({ orderState: initialState }),

    selectAllItems: () => {
        const { items, selectedItems } = get().orderState;
        const { calculateTotals } = get();

        let isSelectedAll = items.length == selectedItems.length;

        const updatedSelectedItems = isSelectedAll ? [] : items.map((item) => item._id);

        set((state) => {
            const order = { orderState: { ...state.orderState, selectedItems: updatedSelectedItems } };

            queueMicrotask(() => {
                calculateTotals();
            });
            return order;
        });
    },
    selectItem: (itemId) => {
        const { selectedItems } = get().orderState;
        const { calculateTotals } = get();

        let updatedSelectedItems;
        if (selectedItems.includes(itemId)) {
            updatedSelectedItems = selectedItems.filter((id) => id !== itemId);
        } else {
            updatedSelectedItems = [...selectedItems, itemId];
        }

        set((state) => {
            const order = { orderState: { ...state.orderState, selectedItems: updatedSelectedItems } };

            queueMicrotask(() => {
                calculateTotals();
            });
            return order;
        });
    },
    changeQuantityItem: (itemId, IncreaseOrQuantity) => {
        const { items } = get().orderState;
        const { calculateTotals } = get();

        const updatedItems = items.map((item) => {
            if (item._id === itemId) {
                // Si es un número, establecer esa cantidad directamente
                if (typeof IncreaseOrQuantity === 'number') {
                    return { ...item, Cantidad: Math.max(1, IncreaseOrQuantity) };
                }
                // Si es boolean, incrementar o decrementar
                return { ...item, Cantidad: IncreaseOrQuantity ? item.Cantidad + 1 : Math.max(1, item.Cantidad - 1) };
            }
            return item;
        });

        set((state) => {
            const order = { orderState: { ...state.orderState, items: updatedItems } };

            queueMicrotask(() => {
                calculateTotals();
            });
            return order;
        });
    },
    calculateTotals: () => {
        const { items, selectedItems, totals, cupon, reglasAutomaticas } = get().orderState;

        const productosSeleccionadosList = items.filter((producto) =>
            selectedItems.includes(producto._id)
        );

        const itemCount = productosSeleccionadosList.reduce((acumulador, producto) => {
            return acumulador + (producto.Cantidad || 0);
        }, 0);

        const subtotal = parseFloat(productosSeleccionadosList.reduce((acumulador, producto) => {
            const unitPrice = producto.product?.public_price || 0;
            const quantity = producto.Cantidad || 0;
            return acumulador + (unitPrice * quantity);
        }, 0).toFixed(2));

        // Aplicar descuento del cupón si está aplicado
        const descuentoCupon = cupon?.aplicado ? (cupon.descuentoAplicable || 0) : 0;
        const aplicarAEnvio = cupon?.cuponData?.AplicarAEnvio || false;
        
        // Aplicar descuentos de reglas automáticas (solo si no hay cupón manual)
        // Cada regla aplica a sus productos específicos
        let descuentoReglasAutomaticas = 0;
        // Crear un mapa de descuentos por producto (idProducto -> descuento) para usar en cálculo de porcentajes
        const descuentosPorProducto = new Map();
        
        console.log("💰 Calculando totales - reglas automáticas:", reglasAutomaticas?.length || 0, "cupón aplicado:", cupon?.aplicado);
        
        if (!cupon?.aplicado && reglasAutomaticas && reglasAutomaticas.length > 0) {
            
            // Primero, normalizar IDs de productos: obtener el master de cada producto
            const productoAMaster = new Map();
            productosSeleccionadosList.forEach(item => {
                let idProducto = null;
                if (item.idModelo) {
                    idProducto = (item.idModelo._id || item.idModelo)?.toString();
                } else if (item.product?._id) {
                    idProducto = item.product._id.toString();
                } else if (item.idProducto) {
                    idProducto = item.idProducto.toString();
                }
                
                // Por ahora, asumimos que el idProducto es el master si no hay idPadre
                // En el backend ya se normalizó, así que aquí usamos el idProducto directamente
                if (idProducto) {
                    productoAMaster.set(item._id.toString(), idProducto);
                }
            });
            
            // Crear un mapa de idProducto -> item del carrito para mapeo rápido
            const productoIdAItem = new Map();
            productosSeleccionadosList.forEach(item => {
                let idProducto = null;
                if (item.idModelo) {
                    idProducto = (item.idModelo._id || item.idModelo)?.toString();
                } else if (item.product?._id) {
                    idProducto = item.product._id.toString();
                } else if (item.idProducto) {
                    idProducto = item.idProducto.toString();
                }
                
                if (idProducto) {
                    // Si ya existe un item con este idProducto, mantener el primero o sumar
                    const existing = productoIdAItem.get(idProducto);
                    if (!existing) {
                        productoIdAItem.set(idProducto, item);
                    }
                }
            });
            
            // Para cada regla, aplicar su descuento a los productos correspondientes
            reglasAutomaticas.forEach(reglaData => {
                const { regla, descuentoAplicable, productosAplicables, idsMasters } = reglaData;
                
                console.log("📋 Procesando regla:", regla.NombreDescuento || regla._id, "descuento:", descuentoAplicable, "productos aplicables:", productosAplicables.length);
                
                if (!descuentoAplicable || descuentoAplicable <= 0) return;
                
                // Usar productosAplicables directamente del backend (ya tiene el mapeo correcto)
                if (productosAplicables && productosAplicables.length > 0) {
                    // Distribuir el descuento proporcionalmente entre los productos de la regla
                    const montoTotalRegla = productosAplicables.reduce((sum, p) => {
                        return sum + (p.precio * p.cantidad);
                    }, 0);
                    
                    console.log("💰 Monto total de la regla:", montoTotalRegla);
                    
                    if (montoTotalRegla > 0) {
                        // Para cada producto aplicable del backend, buscar el item correspondiente en el carrito
                        productosAplicables.forEach(productoBackend => {
                            const idProductoBackend = productoBackend.idProducto?.toString();
                            if (idProductoBackend) {
                                // Buscar todos los items del carrito que corresponden a este producto
                                productosSeleccionadosList.forEach(item => {
                                    let idProductoItem = null;
                                    if (item.idModelo) {
                                        idProductoItem = (item.idModelo._id || item.idModelo)?.toString();
                                    } else if (item.product?._id) {
                                        idProductoItem = item.product._id.toString();
                                    } else if (item.idProducto) {
                                        idProductoItem = item.idProducto.toString();
                                    }
                                    
                                    // Verificar si este item corresponde al producto del backend
                                    if (idProductoItem === idProductoBackend) {
                                        const itemMonto = (item.product?.public_price || 0) * (item.Cantidad || 0);
                                        const proporcion = itemMonto / montoTotalRegla;
                                        const descuentoItem = descuentoAplicable * proporcion;
                                        
                                        const descuentoActual = descuentosPorProducto.get(item._id.toString()) || 0;
                                        descuentosPorProducto.set(item._id.toString(), descuentoActual + descuentoItem);
                                    }
                                });
                            }
                        });
                    }
                } else if (!idsMasters || idsMasters.length === 0) {
                    // Si la regla NO tiene productos específicos, aplica a todos
                    // Distribuir el descuento proporcionalmente entre todos los productos
                    console.log("📋 Regla sin productos específicos, aplicando a todos");
                    if (subtotal > 0) {
                        productosSeleccionadosList.forEach(item => {
                            const itemMonto = (item.product?.public_price || 0) * (item.Cantidad || 0);
                            const proporcion = itemMonto / subtotal;
                            const descuentoItem = descuentoAplicable * proporcion;
                            
                            const descuentoActual = descuentosPorProducto.get(item._id.toString()) || 0;
                            descuentosPorProducto.set(item._id.toString(), descuentoActual + descuentoItem);
                        });
                    }
                }
            });
            
            // Sumar todos los descuentos por producto
            descuentoReglasAutomaticas = Array.from(descuentosPorProducto.values()).reduce((sum, desc) => sum + desc, 0);
            console.log("💰 Descuento total de reglas automáticas:", descuentoReglasAutomaticas);
        }
        
        // Calcular descuento total (cupón o reglas automáticas, pero no ambos)
        const descuentoTotal = cupon?.aplicado ? descuentoCupon : descuentoReglasAutomaticas;
        console.log("💰 Descuento total final:", descuentoTotal);
        
        // Calcular porcentaje y monto de descuento por item
        const porcentajesDescuentoPorItem = {};
        const montosDescuentoPorItem = {};
        
        if (cupon?.aplicado && descuentoCupon > 0 && !aplicarAEnvio) {
            // Si hay cupón aplicado al subtotal, calcular porcentaje efectivo por item
            productosSeleccionadosList.forEach(item => {
                const itemId = item._id.toString();
                const itemMonto = (item.product?.public_price || 0) * (item.Cantidad || 0);
                
                if (subtotal > 0 && itemMonto > 0) {
                    const proporcion = itemMonto / subtotal;
                    const descuentoItem = descuentoCupon * proporcion;
                    
                    if (descuentoItem > 0 && itemMonto > 0) {
                        // Calcular porcentaje efectivo: (descuento / monto) * 100
                        const porcentajeEfectivo = (descuentoItem / itemMonto) * 100;
                        porcentajesDescuentoPorItem[itemId] = Math.round(porcentajeEfectivo * 100) / 100; // Redondear a 2 decimales
                        montosDescuentoPorItem[itemId] = Math.round(descuentoItem * 100) / 100; // Redondear a 2 decimales
                    }
                }
            });
        } else if (!cupon?.aplicado && reglasAutomaticas && reglasAutomaticas.length > 0) {
            // Si hay reglas automáticas, calcular porcentaje por item
            productosSeleccionadosList.forEach(item => {
                const itemId = item._id.toString();
                const descuentoItem = descuentosPorProducto.get(itemId) || 0;
                const itemMonto = (item.product?.public_price || 0) * (item.Cantidad || 0);
                
                if (descuentoItem > 0 && itemMonto > 0) {
                    // Buscar la regla que aplica para obtener el porcentaje si es porcentaje
                    let porcentaje = 0;
                    
                    for (const reglaData of reglasAutomaticas) {
                        const { regla, productosAplicables, idsMasters } = reglaData;
                        
                        // Verificar si este item aplica para esta regla
                        let idProductoItem = null;
                        if (item.idModelo) {
                            idProductoItem = (item.idModelo._id || item.idModelo)?.toString();
                        } else if (item.product?._id) {
                            idProductoItem = item.product._id.toString();
                        } else if (item.idProducto) {
                            idProductoItem = item.idProducto.toString();
                        }
                        
                        let aplicaARegla = false;
                        if (idsMasters && idsMasters.length > 0) {
                            aplicaARegla = productosAplicables.some(p => p.idProducto?.toString() === idProductoItem);
                        } else {
                            aplicaARegla = true;
                        }
                        
                        if (aplicaARegla && regla.TipoDescuento === 0) {
                            // Si es porcentaje, usar el porcentaje de la regla
                            porcentaje = regla.PorcentajeDescuento || 0;
                            break;
                        } else if (aplicaARegla && regla.TipoDescuento === 1) {
                            // Si es monto fijo, calcular porcentaje efectivo
                            const porcentajeEfectivo = (descuentoItem / itemMonto) * 100;
                            porcentaje = Math.round(porcentajeEfectivo * 100) / 100;
                            break;
                        }
                    }
                    
                    if (porcentaje > 0) {
                        porcentajesDescuentoPorItem[itemId] = porcentaje;
                        montosDescuentoPorItem[itemId] = Math.round(descuentoItem * 100) / 100; // Redondear a 2 decimales
                    }
                }
            });
        }
        
        // Si el cupón se aplica al envío, descontar del shipping; si no, del subtotal
        const subtotalConDescuento = aplicarAEnvio ? subtotal : Math.max(0, subtotal - descuentoTotal);
        const shippingConDescuento = aplicarAEnvio 
            ? Math.max(0, (totals.shipping || 0) - descuentoCupon)
            : (totals.shipping || 0);

        let total = parseFloat((subtotalConDescuento + shippingConDescuento).toFixed(2));
        let balanceApplied = parseFloat(totals.balanceAvailable - total).toFixed(2);
        if (balanceApplied < 0) {
            balanceApplied = totals.balanceAvailable;
        }
        total -= balanceApplied;
        if (total < 0) total = 0;

        set((state) => {
            // Asegurar que shipping se mantenga del estado actual si no se está actualizando
            const shippingActual = state.orderState.totals.shipping || 0;
            return { orderState: { ...state.orderState, 
                porcentajesDescuentoPorItem: porcentajesDescuentoPorItem, // Guardar porcentajes de descuento por item
                montosDescuentoPorItem: montosDescuentoPorItem, // Guardar montos de descuento por item
                totals: { ...state.orderState.totals, 
                    subtotal, 
                    total, 
                    itemCount, 
                    balanceApplied,
                    discount: descuentoTotal,
                    shipping: shippingActual // Mantener el shipping actualizado
                } 
            }};
        });
    },
}), {
    name: 'orderStore',
    storage: createJSONStorage(() => localStorage),
}))
